import React from 'react';
import { Smartphone, Building2, Sparkles, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const mobileMoney = [
  { name: 'M-Pesa', color: '#00A859', initials: 'MP' },
  { name: 'Airtel Money', color: '#ED1C24', initials: 'AM' },
  { name: 'Orange Money', color: '#FF7900', initials: 'OM' }
];

const banks = [
  { name: 'BGFI Bank', color: '#003B71', initials: 'BG' },
  { name: 'Ecobank', color: '#0066B3', initials: 'EB' },
  { name: 'Equity BCDC', color: '#E30613', initials: 'EQ' },
  { name: 'Rawbank', color: '#005A9C', initials: 'RB' },
  { name: 'Illicocash', color: '#FFC107', initials: 'IC' }
];

const Payments = () => {
  const { t } = useLanguage();

  return (
    <section id="paiements" className="py-24 bg-gradient-to-br from-blue-50 via-white to-blue-50/40 relative overflow-hidden">
      {/* Decorative blur circles */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full mb-4">
            <Sparkles className="text-blue-600" size={18} />
            <span className="text-sm font-semibold text-blue-700">{t('paymentsBadge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('paymentsTitle')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('paymentsSubtitle')}
          </p>
        </div>

        {/* Highlight badge */}
        <div className="max-w-3xl mx-auto mb-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 shadow-xl text-white text-center">
          <div className="flex items-start gap-4 justify-center">
            <CheckCircle2 className="flex-shrink-0 mt-1" size={28} />
            <div className="text-left">
              <p className="font-bold text-lg mb-1">{t('paymentsConvenience')}</p>
              <p className="text-blue-100">{t('paymentsConvenienceDesc')}</p>
            </div>
          </div>
        </div>

        {/* Mobile Money */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
              <Smartphone className="text-white" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{t('mobileMoneyTitle')}</h3>
              <p className="text-sm text-gray-500">{t('mobileMoneyDesc')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {mobileMoney.map((p, idx) => (
              <div
                key={idx}
                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border border-gray-100"
                data-testid={`payment-mobile-${idx}`}
              >
                <div className="h-2" style={{ backgroundColor: p.color }} />
                <div className="p-5 flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md group-hover:scale-110 transition-transform duration-300"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.initials}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500">Mobile Money</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bank Transfer */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
              <Building2 className="text-white" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{t('bankTransferTitle')}</h3>
              <p className="text-sm text-gray-500">{t('bankTransferDesc')}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {banks.map((b, idx) => (
              <div
                key={idx}
                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border border-gray-100"
                data-testid={`payment-bank-${idx}`}
              >
                <div className="h-2" style={{ backgroundColor: b.color }} />
                <div className="p-4 text-center">
                  <div
                    className="w-14 h-14 mx-auto rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md mb-3 group-hover:scale-110 transition-transform duration-300"
                    style={{ backgroundColor: b.color }}
                  >
                    {b.initials}
                  </div>
                  <div className="font-bold text-gray-900 text-sm leading-tight">{b.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom info */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 italic">
            {t('paymentsFooter')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Payments;
